import {
  DEFAULT_PIPELINE_DEFINITION,
  getPhaseLabels,
  isValidPhase,
  PHASES_ORDER,
  type ActivityCockpitSnapshot,
  type CockpitArtifact,
  type Phase,
} from '@internal/spassu-code-contract';
import type { CockpitPhaseStepView } from './cockpitViewModel';

/**
 * The stepper only ever renders a phase the platform recorded: the summary
 * phase first (RN-COCK-01), then the last phase boundary observed in the audit
 * log for snapshots stored before the summary carried it. With neither, the
 * result is `null` and no stepper is drawn — a guessed phase would be worse
 * than none. Steps carry a synthetic key and the contract label, never the
 * internal phase code (ADR-005, UT-051). Each step also lists the artifacts
 * of its phase (RN-COCK-08) and names the one to open on selection
 * (RN-COCK-09): the type the pipeline expects for the phase, or the only one.
 */
export function createCockpitPhaseSteps(
  snapshot: ActivityCockpitSnapshot,
): CockpitPhaseStepView[] | null {
  const current = currentPhase(snapshot);
  if (!current) return null;
  const labels = getPhaseLabels(DEFAULT_PIPELINE_DEFINITION);
  const completed =
    snapshot.sections.summary.value?.activity.status === 'completed';
  const currentIndex = PHASES_ORDER.indexOf(current);
  const artifacts = snapshot.sections.artifacts.value ?? [];
  return PHASES_ORDER.map((phase, index) => {
    const ofPhase = artifacts.filter(artifact => artifact.phase === phase);
    return {
      key: `phase-${index + 1}`,
      label: labels[phase],
      state: stepState(index, currentIndex, completed),
      artifactIds: ofPhase.map(artifact => artifact.id),
      artifactId: mainArtifactId(phase, ofPhase),
    };
  });
}

function mainArtifactId(
  phase: Phase,
  ofPhase: CockpitArtifact[],
): string | null {
  if (ofPhase.length === 1) return ofPhase[0].id;
  const expectedType = DEFAULT_PIPELINE_DEFINITION.phases.find(
    definition => definition.id === phase,
  )?.artifactType;
  const expected = expectedType
    ? ofPhase.filter(artifact => artifact.artifactType === expectedType)
    : [];
  return expected.at(-1)?.id ?? null;
}

function currentPhase(snapshot: ActivityCockpitSnapshot): Phase | null {
  const reported = snapshot.sections.summary.value?.activity.phase;
  if (typeof reported === 'string' && isValidPhase(reported)) return reported;
  const boundaries = snapshot.sections.time.value?.phaseBoundaries ?? [];
  for (let index = boundaries.length - 1; index >= 0; index -= 1) {
    const target = boundaries[index].to;
    if (isValidPhase(target)) return target;
  }
  return null;
}

function stepState(
  index: number,
  currentIndex: number,
  completed: boolean,
): CockpitPhaseStepView['state'] {
  if (completed || index < currentIndex) return 'completed';
  return index === currentIndex ? 'active' : 'pending';
}
